import { type FC, useState } from 'react';
import { cubicBezier, motion, type Variants } from 'framer-motion';

import { MotionLightColorEffect } from '@/components/common/LightColorEffect/MotionLightColorEffect';
import { MotionPicture } from '@/components/ui/Picture/Picture';
import { useAppSelector } from '@/redux/store';

import { InitView } from './InitView/InitView';
import { MainView } from './MainView/MainView';
import { endBombAnimationVariant, startBombAnimationVariant } from './config';

import css from './styles.module.scss';

import bombPng from '/img/dice-game/bomb-very-huge.png';
import bombWebp from '/img/dice-game/bomb-very-huge.webp';

interface BombEventProps {
  isBombEventActive: boolean;
  closeBombEvent: () => void;
}

const BombEvent: FC<BombEventProps> = ({ closeBombEvent }) => {
  const [bombPosition, setBombPosition] = useState<null | {
    x: number;
    y: number;
  }>(null);
  const [animationVariant, setAnimationVariant] = useState<'start' | 'end'>(
    'start',
  );
  const initialBombPosition = useAppSelector(
    (state) => state.diceGame.position.bombImage,
  );

  const changeBombPosition = (args: { x: number; y: number }): void => {
    setBombPosition(args);
  };

  const changeAnimationVariant = (variant: 'start' | 'end'): void => {
    setAnimationVariant(variant);
  };

  const bombAnimationVariants: Variants = {
    start: bombPosition ? startBombAnimationVariant : {},
    end: endBombAnimationVariant,
  };

  const wrapperAnimationVariants: Variants = {
    start: {
      ...bombPosition,
      transition: { duration: 0.5, ease: cubicBezier(0, 1, 1, 1) },
    },
    end: {
      ...bombPosition,
      opacity: 0,
      transition: {
        delay: 0.6,
        duration: 0.2,
        ease: 'easeOut',
      },
    },
  };

  return (
    <div className="fixed z-[999] w-full h-full top-0 left-0">
      <MainView
        animationVariant={animationVariant}
        changeBombPosition={changeBombPosition}
        closeBombEvent={closeBombEvent}
        changeAnimationVariant={changeAnimationVariant}
      />
      <InitView animationVariant={animationVariant} />

      <motion.div
        initial={initialBombPosition || undefined}
        variants={wrapperAnimationVariants}
        animate={animationVariant}
        className="absolute aspect-square w-[169px] z-[3] isolate"
      >
        <div className="relative w-full h-full">
          <MotionPicture
            initial={{ width: 72 }}
            variants={bombAnimationVariants}
            animate={animationVariant}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 aspect-square z-[2]"
            img={bombPng}
            webp={bombWebp}
          />
          <MotionLightColorEffect
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 1 }}
            lightClassName={css.redGradient}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[45%] w-[153.03%] pointer-events-none"
          />
        </div>
      </motion.div>
    </div>
  );
};

export { BombEvent };
